// modules
import { fundsService } from 'modules/Funds';
import NotificationsService from 'modules/Notifications';
import FormatService from 'modules/Format';
import SearchService from 'modules/Search';
import UserService from 'modules/User';
import BrokerageAndCharges from 'modules/BrokerageAndCharges';
import InstrumentData from 'modules/InstrumentData';
import PreRiskService from 'modules/PreRisk';
import { LStorage } from 'modules/Storage';
import PositionsTVService from 'modules/TradesPW3/Positions/PositionsTvService/PositionsTVService';
import PositionsPW3Service from 'modules/TradesPW3/Positions/positions';
import HoldingsTvService from 'modules/TradesPW3/Holdings/holdingsTv/HoldingsTvService';
import OrdersTVService from 'modules/TradesPW3/Orders';
import OrderingService, { CommonOrderDuration } from 'modules/Ordering';
import Validation from 'modules/Validation';
import AnalyticsService from 'modules/Analytics/service';
import LocaleService from 'modules/Localization';
import { decomposeExchangeToken } from 'modules/BOD/modules/Search/utils';
// constants
import {
  orderTableColumns,
  orderFormCustomFields,
} from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/orders/columns';
import { fundsTableColumns } from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/funds/columns';
import {
  closedPositionsTableColumns,
} from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/positions/closedPositionsColumns';
import { EVENTS } from 'modules/Analytics/consts';
import { holdingTableColumns } from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/holdings/columns';
import { positionsTableColumns } from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/positions/positionsColumns';
import { notificationTableColumns } from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/notifications/columns';
// typings
import { ITVPosition } from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/positions/types';
import { ITVNotification } from './notifications/types';
import { ITVFunds } from './funds/types';
import { ITVOrder } from './orders/types';
import { ITVHolding } from './holdings/types';
import {
  AccountManagerInfo,
  AccountManagerSummaryField,
  ActionMetaInfo,
  Execution,
  IDelegate,
  InstrumentInfo,
  TradeContext,
  IWatchedValue,
  Order,
  AccountId,
  OrderPreviewResult,
  IsTradableResult,
  PlaceOrderResult,
  MenuSeparator,
  BrokerConfigFlags,
  OrderDialogOptions,
  AccountManagerPage,
} from '../../../sources/charting_library.min';
import {
  ConnectionStatus,
  Side as TvSide,
  OrderType as TvOrderType,
  NotificationType,
  StandardFormatterName,
} from '../../../sources/charting_library';
import {
  IUpdatedBrokerWithoutRealtime,
  IUpdatedBrokerConnectionAdapterHost,
} from './typings';
import { addSingleScripSurveillanceEvent } from '../../../components/SurveillanceMessages/utils';

interface AccountManagerData {
  holdingsTotalPnl: string;
  todayPositionsPnl: string;
  totalPositionsPnl: string;
}

class Broker implements IUpdatedBrokerWithoutRealtime {
  private readonly _host: IUpdatedBrokerConnectionAdapterHost;

  private readonly _accountManagerData: AccountManagerData = {
    holdingsTotalPnl: FormatService.addRupeeSign(
      FormatService.formatToIndianFloat(0, 2, true),
      true,
    ),
    todayPositionsPnl: FormatService.addRupeeSign(
      FormatService.formatToIndianFloat(0, 2, true),
      true,
    ),
    totalPositionsPnl: FormatService.addRupeeSign(
      FormatService.formatToIndianFloat(0, 2, true),
      true,
    ),
  };

  private readonly _amChangeDelegate: IDelegate<
    (values: AccountManagerData) => void
  >;

  private readonly _positionsDelegate: IDelegate<(values: ITVPosition) => void>;

  private readonly _holdingsDelegate: IDelegate<(values: ITVHolding) => void>;

  private readonly _fundsDelegate: IDelegate<(values: ITVFunds) => void>;

  private readonly _notificationsDelegate:
    IDelegate<(values: ITVNotification) => void>;

  private readonly _holdingsTotalPnL: IWatchedValue<string>;
  private readonly _todayPositionsPnl: IWatchedValue<string>;
  private readonly _totalPositionsPnl: IWatchedValue<string>;

  private readonly _executions: Execution[] = [];

  public constructor(
    host: IUpdatedBrokerConnectionAdapterHost,
  ) {
    this._host = host;

    this._amChangeDelegate = this._host.factory.createDelegate();
    this._holdingsTotalPnL = this._host.factory.createWatchedValue(
      this._accountManagerData.holdingsTotalPnl,
    );
    this._todayPositionsPnl = this._host.factory.createWatchedValue(
      this._accountManagerData.todayPositionsPnl,
    );
    this._totalPositionsPnl = this._host.factory.createWatchedValue(
      this._accountManagerData.totalPositionsPnl,
    );

    this._amChangeDelegate.subscribe(null, (values: AccountManagerData) => {
      this._holdingsTotalPnL.setValue(values.holdingsTotalPnl);
      this._todayPositionsPnl.setValue(values.todayPositionsPnl);
      this._totalPositionsPnl.setValue(values.totalPositionsPnl);
    });

    this._positionsDelegate = this._host.factory.createDelegate();
    this._fundsDelegate = this._host.factory.createDelegate();
    this._holdingsDelegate = this._host.factory.createDelegate();
    this._notificationsDelegate = this._host.factory.createDelegate();
  }

  public isDomPanelVisible() {
    return this._host.domPanelVisibility()?.value();
  }

  public isInstantOrderEnabled() {
    return this._host.silentOrdersPlacement()?.value();
  }

  public showConfirmationDialog(
    heading: string,
    content: string,
    cancelText: string,
    confirmText: string,
    neverShowOption = true,
  ) {
    return this._host.showConfirmDialog(
      heading,
      content,
      cancelText,
      confirmText,
      neverShowOption,
    );
  }

  public connectionStatus(): ConnectionStatus {
    return ConnectionStatus.Connected;
  }

  public chartContextMenuActions(
    context: TradeContext,
  ): Promise<ActionMetaInfo[]> {
    return this._host.defaultContextMenuActions(context);
  }

  public async isTradable(symbol: string): Promise<boolean | IsTradableResult> {
    const exchangeToken =
      FormatService.decodeCustomTvExchangeToken(symbol || '');
    const scripData = await SearchService.searchByExchangeToken(exchangeToken);
    const isIndex = Validation.isIndex(scripData?.instrument || 'INDEX');

    return Promise.resolve(isIndex ? {
      tradable: false,
      reason: LocaleService.translate('NON_TRADABLE_REASON', exchangeToken, scripData?.displayName || ''),
    } : {
      tradable: true,
    });
  }

  public async placeOrder(
    preOrder: ExtendedPreOrder,
  ): Promise<PlaceOrderResult> {
    const exchangeToken = FormatService.decodeCustomTvExchangeToken(preOrder.symbol || '');
    const { segment } = decomposeExchangeToken(exchangeToken);
    const { isBlocked, asbaErrorMessage } = UserService.isOrderingBlockedByASBA(
      segment,
    );
    if (isBlocked) return Promise.reject(new Error(asbaErrorMessage));

    let isAccepted = true;
    const instantOrderEnabled = this._host?.silentOrdersPlacement()?.value();
    if (instantOrderEnabled) {
      isAccepted = await this._host.showConfirmDialog(
        LocaleService.translate('PLACE_ORDER_AT_MARKET_PRICE'),
        LocaleService.translate('INSTANT_ORDER_DISCLAIMER'),
        LocaleService.translate('PLACE_ORDER'),
        LocaleService.translate('CANCEL_BUTTON'),
        true,
      );
    }

    const scripData = await SearchService.searchByExchangeToken(exchangeToken);
    const instrumentKey = scripData?.instrumentKey;

    const preRiskWarningMsg = await PreRiskService.getPreRiskWarningForSingleScrip(instrumentKey || '');
    let underSurveillance = false;

    if (preRiskWarningMsg && isAccepted) {
      underSurveillance = true;
      isAccepted = await this._host.showConfirmDialog(
        LocaleService.translate('SURVEILLANCE_WARNING'),
        preRiskWarningMsg,
        LocaleService.translate('PRERISK_CONTINUE'),
        LocaleService.translate('CANCEL_BUTTON'),
        false,
      );

      addSingleScripSurveillanceEvent(
        scripData,
        isAccepted,
        'Order entry',
      );
    }

    if (isAccepted) {
      const product = preOrder.customFields?.productType;
      // Handle CO/OCO position square off when instant order is enabled
      if (product === 'CO' || product === 'OCO') {
        await PositionsPW3Service.squareOffCOPosition(instrumentKey ?? '');
        return { orderId: '' };
      }
      const lastProductSelection = LStorage.getItem('LAST_PRODUCT_TYPE_SELECTION') || 'I';

      const orderWithCustomFields: OrderWithCustomFields = {
        ...preOrder,
        symbol: exchangeToken,
        instrumentKey: instrumentKey || '',
        duration: (preOrder.duration || { type: 'DAY' }) as CommonOrderDuration,
        ...(!preOrder.customFields ? {
          customFields: {
            productType: Validation.isFnO(segment) ? 'D' : lastProductSelection as Product,
          },
        } : {
          customFields: {
            productType: preOrder.customFields.productType,
          },
        }),
      };

      if (!preOrder.customFields) {
        orderWithCustomFields.customFields = {
          productType: Validation.isFnO(segment) ? 'D' : lastProductSelection as Product,
        };
      }

      if (!Validation.isFnO(segment)) {
        LStorage.setItem(
          'LAST_PRODUCT_TYPE_SELECTION',
          preOrder.customFields?.productType || 'I',
        );
      }

      return OrderingService.preOrderPlace(
        orderWithCustomFields,
        underSurveillance,
      );
    }

    return { orderId: '' };
  }

  public async previewOrder(
    order: ExtendedPreOrder,
  ): Promise<OrderPreviewResult> {
    const exchangeToken = FormatService.decodeCustomTvExchangeToken(order.symbol || '');
    const { segment } = decomposeExchangeToken(exchangeToken);
    // eslint-disable-next-line max-len
    const { isBlocked, asbaErrorMessage } = UserService.isOrderingBlockedByASBA(segment);
    if (isBlocked) return Promise.reject(new Error(asbaErrorMessage));

    try {
      const isDeliveryOrder = order.customFields?.productType === 'D' || false;
      const plan = (UserService.profile?.pricingPlan ?? 'basic').toLowerCase();

      const [brokerage, lastTradingDate] = await Promise.all([
        BrokerageAndCharges.getChargesForSingleScripOrder(order, plan),
        InstrumentData.getLastTradingDate(order),
      ]);
      return BrokerageAndCharges.getFormattedOrderPreview(
        brokerage?.data,
        isDeliveryOrder,
        order.side,
        lastTradingDate,
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // InstrumentData service has all the error handling for getLastTradingDate
      AnalyticsService.sendEvent(
        EVENTS.BROKERAGE_AND_CHARGES_ERROR,
        {
          'Error message': (error as Record<string, unknown>)?.message ?? error,
          'Error code': (error as Record<string, unknown>)?.code ?? '',
        },
        true,
      );
      return BrokerageAndCharges.getFormattedOrderPreviewError('');
    }
  }

  public async modifyOrder(order: Order): Promise<void> {
    const exchangeToken = FormatService.decodeCustomTvExchangeToken(order.symbol || '');
    const { segment } = decomposeExchangeToken(exchangeToken);
    const { isBlocked, asbaErrorMessage } = UserService.isOrderingBlockedByASBA(
      segment,
    );
    if (isBlocked) return Promise.reject(new Error(asbaErrorMessage));

    const updatedOrder = {
      ...order,
      symbol: exchangeToken,
    };
    return OrderingService.modifyOrder(
      updatedOrder as unknown as OrderWithCustomFields,
    );
  }

  public async closePosition(positionId: string): Promise<void> {
    const positionById = PositionsPW3Service.getPositionByUniqueId(
      PositionsPW3Service.positions,
      positionId,
    );

    if (positionById) {
      const position = PositionsTVService.formatPositionToTVTable(positionById);
      const { segment } = decomposeExchangeToken(position.exchangeToken);
      const {
        isBlocked,
        asbaErrorMessage,
      } = UserService.isOrderingBlockedByASBA(segment);
      if (isBlocked) return Promise.reject(new Error(asbaErrorMessage));
      if (positionById?.product === 'MTF' || positionById?.product === 'MTFS') {
        PositionsTVService.openClosePositionModal(positionId);
        return;
      }

      const handler = () => {
        const order: ExtendedPreOrder = {
          symbol: position.symbol,
          side: position.side === TvSide.Sell ? TvSide.Buy : TvSide.Sell,
          type: TvOrderType.Market,
          qty: Math.abs(Number(position.netQty)),
          duration: {
            type: 'DAY',
          },
          customFields: {
            productType: positionById?.product,
          },
          seenPrice: null,
        };
        this.placeOrder(order);
      };

      handler();
    }
  }

  public orders(): Promise<ITVOrder[]> {
    return OrdersTVService.getOrdersData();
  }

  public async positions(): Promise<ITVPosition[]> {
    return PositionsTVService.fetchTVFormattedPositions();
  }

  public executions(symbol: string): Promise<Execution[]> {
    const exchangeToken =
      FormatService.decodeCustomTvExchangeToken(symbol || '');
    return Promise.resolve(
      this._executions.filter(
        (data: Execution) => data.symbol === exchangeToken,
      ),
    );
  }

  public async cancelOrder(orderId: string): Promise<void> {
    const order = OrdersTVService.findOrderById(orderId);
    const { isBlocked, asbaErrorMessage } = UserService.isOrderingBlockedByASBA(
      order?.instrument.e,
    );
    if (isBlocked) return Promise.reject(new Error(asbaErrorMessage));

    return Promise.resolve(OrderingService.cancelOrder(orderId));
  }

  public async cancelOrders(
    symbol: string,
    side: TvSide | undefined,
    ordersIds: string[],
  ): Promise<void> {
    const closeHandler = () => Promise.all(
      ordersIds.map((orderId: string) => this.cancelOrder(orderId)),
    );

    await closeHandler();
  }

  public accountManagerInfo(): AccountManagerInfo {
    const showNotificationTab = false;

    const summaryProps: AccountManagerSummaryField[] = [
      {
        text: "Today's Positions P&L",
        wValue: this._todayPositionsPnl,
        formatter: StandardFormatterName.Text,
      },
      {
        text: 'Total Positions P&L',
        wValue: this._totalPositionsPnl,
        formatter: StandardFormatterName.Text,
      },
      {
        text: 'Holdings P&L',
        wValue: this._holdingsTotalPnL,
        formatter: StandardFormatterName.Text,
      },
    ];
    const pages: AccountManagerPage[] = [
      {
        id: 'funds',
        title: 'Funds',
        tables: [
          {
            id: 'funds',
            columns: fundsTableColumns,
            // eslint-disable-next-line @typescript-eslint/ban-types
            getData: async () => fundsService.getFundsData() as Promise<{}[]>,
            changeDelegate: this._fundsDelegate,
          },
        ],
      },
      {
        id: 'closedPositionsPage',
        title: 'Closed Positions',
        tables: [
          {
            id: 'closedPositionsTable',
            columns: closedPositionsTableColumns,
            getData: () =>
              Promise.resolve(
                PositionsTVService.fetchTVFormattedClosedPositions(),
              ),
            changeDelegate: this._positionsDelegate,
          },
        ],
      },
      {
        id: 'holdingsPage',
        title: 'Holdings',
        tables: [
          {
            id: 'holdingsTable',
            columns: holdingTableColumns,
            getData: async () => HoldingsTvService.fetchFormattedHoldings(),
            changeDelegate: this._holdingsDelegate,
          },
        ],
      },
    ];

    if (showNotificationTab) {
      pages.push({
        id: 'notifications',
        title: 'Notifications Log',
        tables: [
          {
            id: 'notifications',
            columns: notificationTableColumns,
            getData: () =>
              // eslint-disable-next-line @typescript-eslint/ban-types
              NotificationsService.getNotificationData() as Promise<{}[]>,
            changeDelegate: this._notificationsDelegate,
          },
        ],
      });
    }

    return {
      accountTitle: '',
      summary: summaryProps,
      orderColumns: orderTableColumns,
      orderColumnsSorting: {
        property: 'time',
        asc: false,
      },
      positionColumns: positionsTableColumns,
      pages,
      contextMenuActions: (
        contextMenuEvent: MouseEvent,
        activePageActions: ActionMetaInfo[],
      ) => Promise.resolve(this._bottomContextMenuItems(
        contextMenuEvent,
        activePageActions,
      )),
    };
  }

  public async symbolInfo(symbol: string): Promise<InstrumentInfo> {
    const exchangeToken =
      FormatService.decodeCustomTvExchangeToken(symbol || '');
    const {
      tickSize = 0,
      lotSize = 1,
      maxQuantity,
      displayName,
      exchange,
      segment,
    } = await SearchService.searchByExchangeToken(exchangeToken) || {};

    const mintick = tickSize / 100;
    const pipSize = mintick; // pip size can differ from minTick
    const accountCurrencyRate = 1; // account currency rate
    const pointValue = 1; // USD value of 1 point of price
    const updatedLotSize = Validation.isCommodity(segment) ? 1 : lotSize;

    return {
      qty: {
        min: updatedLotSize,
        max: maxQuantity || Number.MAX_VALUE,
        step: updatedLotSize,
        uiStep: updatedLotSize,
      },
      pipValue: pipSize * pointValue * accountCurrencyRate || 1,
      pipSize,
      minTick: mintick,
      units: 'Quantity',
      lotSize: updatedLotSize,
      brokerSymbol: `${displayName} ${exchange}`,
      description: '',
    };
  }

  getOrderDialogOptions(
    symbol: string,
  ): Promise<OrderDialogOptions | undefined> {
    const exchangeToken =
      FormatService.decodeCustomTvExchangeToken(symbol || '');

    const { segment } = decomposeExchangeToken(exchangeToken);
    const isFnO = Validation.isFnO(segment);
    const lastProductSelection = LStorage.getItem('LAST_PRODUCT_TYPE_SELECTION') || 'I';

    return Promise.resolve({
      symbol: exchangeToken,
      customFields: orderFormCustomFields(isFnO, lastProductSelection),
    } as OrderDialogOptions);
  }

  public showOrderDialog = (order: ExtendedPreOrder) =>
    this._host?.showOrderDialog?.(order)

  public currentAccount(): AccountId {
    return '1' as AccountId;
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  public async accountsMetainfo() {
    return [
      {
        id: '1' as AccountId,
        name: '',
      },
    ];
  }

  public updateClosedPositions = (data: ITVPosition[]) => {
    data.forEach(item => {
      this._positionsDelegate.fire(item);
    });
  };

  public updateOpenPositions = (data: ITVPosition[]) => {
    data.forEach(item => {
      this._host.positionUpdate(item);
    });
  };

  private _bottomContextMenuItems(
    contextMenuEvent: MouseEvent,
    activePageActions: ActionMetaInfo[],
  ): ActionMetaInfo[] {
    const separator: MenuSeparator = { separator: true };
    const sellBuyButtonsVisibility = this._host.sellBuyButtonsVisibility();

    const isPositionPage = activePageActions.some(action => (
      action.text === 'Close Position' ||
      action.text === 'Modify Position'
    ));

    if (isPositionPage) {
      const positionRow = (contextMenuEvent.target as HTMLElement)?.closest('tr');
      const positionId = positionRow?.dataset?.rowId;
      if (positionId) {
        const isPositionConvertible =
          PositionsPW3Service.isPositionConvertible(
            positionId,
          );
        if (isPositionConvertible) {
          activePageActions.push({
            text: 'Convert Position',
            action: () => {
              PositionsPW3Service.openConvertPositionModal(positionId);
            },
          });
        }
      }
    }

    if (activePageActions.length) {
      activePageActions.push(separator);
    }

    return activePageActions.concat([
      {
        text: 'Show Buy/Sell Buttons',
        action: () => {
          if (sellBuyButtonsVisibility) {
            sellBuyButtonsVisibility.setValue(
              !sellBuyButtonsVisibility.value(),
            );
          }
        },
        checkable: true,
        checked:
          sellBuyButtonsVisibility !== null && sellBuyButtonsVisibility.value(),
      },
      {
        text: 'Trading Settings...',
        action: () => {
          this._host.showTradingProperties();
        },
      },
    ]);
  }

  public updateHoldingsTotalPnL(): void {
    this._accountManagerData.holdingsTotalPnl =
      HoldingsTvService.getFormattedHoldingsPnL();
    this._amChangeDelegate.fire(this._accountManagerData);
  }

  public updatePositionsPnL(): void {
    const { daily, total } = PositionsTVService.getFormattedPositionsPnL();

    PositionsPW3Service.setPositionsPnL(daily, total);
    this._accountManagerData.todayPositionsPnl = daily;
    this._accountManagerData.totalPositionsPnl = total;
    this._amChangeDelegate.fire(this._accountManagerData);
  }

  public updateHoldings = (data: ITVHolding[]) => {
    data.forEach(item => {
      this._holdingsDelegate.fire(item);
    });
  };

  public updateOrders = (data: ITVOrder[]) => {
    data.forEach(item => {
      this._host.orderUpdate(item);
    });
  };

  public updateNotifications = (data: ITVNotification[]) => {
    data.forEach(item => {
      this._notificationsDelegate.fire(item);
    });
  };

  public updateFunds = (data: ITVFunds[]) => {
    data.forEach(item => {
      this._fundsDelegate.fire(item);
    });
  };

  public showNotification(
    title: string,
    text: string,
    notificationType?: NotificationType,
  ): void {
    this._host.showNotification(title, text, notificationType);
  }

  public plUpdate = (positionId: string, pnl: number) => {
    this._host.plUpdate(positionId, pnl);
  }

  public showWatchlistMaxScripsValidationModal() {
    this._host.showMessageDialog(
      LocaleService.translate('WATCHLIST'),
      LocaleService.translate('WATCHLIST_MAXIMUM_LENGTH_MESSAGE'),
      true,
    );
  }

  public patchConfig(config: Partial<BrokerConfigFlags>) {
    this._host.patchConfig(config);
    this._host.currentAccountUpdate();
  }
}

export default Broker;
